import type { DmNotification, DmNotificationPatientRow } from '../../../services/dmNotifications';
import { isPatientExpanded, type InboxFolds } from './inboxFolds.ts';

// One rendered line: a patient, or one of its notifications spliced under it when expanded.
export type NotificationTableRow =
    | {
          kind: 'patient';
          id: string;
          patient: DmNotificationPatientRow;
          expanded: boolean;
          leaving: boolean;
      }
    | {
          kind: 'notification';
          id: string;
          patient: DmNotificationPatientRow;
          notification: DmNotification;
          leaving: boolean;
      };

export function isNotificationRow(
    row: NotificationTableRow
): row is Extract<NotificationTableRow, { kind: 'notification' }> {
    return row.kind === 'notification';
}

export interface NotificationRowState {
    folds: InboxFolds;
    leavingIds: ReadonlySet<string>;
    hiddenIds: ReadonlySet<string>;
}

function patientRows(
    patient: DmNotificationPatientRow,
    { folds, leavingIds, hiddenIds }: NotificationRowState
): NotificationTableRow[] {
    const visible = patient.notifications.filter((n) => !hiddenIds.has(n.id));
    // Its last notification was reviewed: the patient goes with it.
    if (patient.notifications.length > 0 && visible.length === 0) return [];
    const expanded = isPatientExpanded(folds, patient.patientId);
    const row: NotificationTableRow = {
        kind: 'patient',
        id: `patient:${patient.patientId}`,
        patient,
        expanded,
        leaving: visible.length > 0 && visible.every((n) => leavingIds.has(n.id))
    };
    if (!expanded) return [row];
    return [
        row,
        ...visible.map((notification): NotificationTableRow => ({
            kind: 'notification',
            id: `notification:${notification.id}`,
            patient,
            notification,
            leaving: leavingIds.has(notification.id)
        }))
    ];
}

export function countPatientRows(rows: readonly NotificationTableRow[]): number {
    return rows.filter((row) => row.kind === 'patient').length;
}

// Spliced after the server page, so a page always holds the same number of patients.
export function expandNotificationRows(
    patients: readonly DmNotificationPatientRow[],
    state: NotificationRowState
): NotificationTableRow[] {
    return patients.flatMap((patient) => patientRows(patient, state));
}
